import Router from 'next/router';
import { useContext, useEffect, useMemo, useRef } from 'react';
import SortOption from '~/constants/common/SortOption';
import {
  paramsToRemove,
  utmContentToRemove
} from '~/constants/srp/paramsToRemove';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import { useDealershipId } from '~/context/dealership/StaticContext';
import SearchContext from '~/context/srp/SearchContext';
import { stringifyAndEncode, stripEquals } from '~/utilities/base64';
import isServerSideRendering from '~/utilities/isServerSideRendering';
import scrollToTop from '~/utilities/scrollToTop';

const emptyObjects = [
  stripEquals(stringifyAndEncode({})),
  stripEquals(stringifyAndEncode({ filters: {} }))
];

function useRemoveQueryParams(urlIn: URL) {
  return useMemo(() => {
    const urlOut = new URL(urlIn.toString());

    paramsToRemove.forEach(param => {
      urlOut.searchParams.delete(param);
    });

    utmContentToRemove.forEach(v => {
      if (urlOut.searchParams.get('utm_content') === v) {
        urlOut.searchParams.delete('utm_content');
      }
    });

    return urlOut;
  }, [urlIn]);
}

function useSetCvnaId(urlIn: URL) {
  const context = useContext(AppliedFiltersContext);
  const dealershipId = useDealershipId();
  const sortBy =
    context.sortBy === SortOption.MostPopular ? undefined : context.sortBy;

  return useMemo(() => {
    if (!context.filtersOrSortDidChange) return urlIn; // Filters & sort by have not changed, do not touch the cvnaid

    const useDealershipPathname =
      dealershipId && !isServerSideRendering
        ? window.location.pathname.includes('/dealership/')
        : false;

    const urlOut = new URL(urlIn.toString());
    const cvnaId = stripEquals(
      stringifyAndEncode({ filters: context.appliedFilters, sortBy })
    );

    if (emptyObjects.includes(cvnaId)) {
      urlOut.pathname = useDealershipPathname
        ? `/dealership/${dealershipId}/cars`
        : dealershipId
        ? '/cars'
        : '/cars-v1';
      urlOut.searchParams.delete('cvnaid');
    } else {
      urlOut.pathname = useDealershipPathname
        ? `/dealership/${dealershipId}/cars/filters`
        : dealershipId
        ? '/cars/filters'
        : '/cars-v1/filters';
      urlOut.searchParams.set('cvnaid', cvnaId);
    }

    return urlOut;
  }, [
    context.appliedFilters,
    context.filtersOrSortDidChange,
    dealershipId,
    sortBy,
    urlIn
  ]);
}

function useSetPageNumber(urlIn: URL) {
  const { pageNumber } = useContext(AppliedFiltersContext);

  return useMemo(() => {
    const urlOut = new URL(urlIn.toString());

    if (pageNumber <= 1) {
      urlOut.searchParams.delete('page');
    } else {
      urlOut.searchParams.set('page', String(pageNumber));
    }

    return urlOut;
  }, [pageNumber, urlIn]);
}

export default function useSyncUrl() {
  const { showFinance } = useContext(SearchContext);
  const isInitializedRef = useRef(false);

  const url = useSetCvnaId(
    useSetPageNumber(
      useRemoveQueryParams(
        new URL(
          isServerSideRendering ? 'https://www.dummy.com' : window.location.href
        )
      )
    )
  );
  // convert to string to prevent multiple re-renders in useEffect
  const urlString = useMemo(() => url.toString(), [url]);

  useEffect(() => {
    if (window.location.href === urlString) {
      isInitializedRef.current = true;
    } else {
      // Scroll to top of page when `url` changes (filters, pagination, etc.)
      scrollToTop().then(() => {
        const { query } = Router;

        // Update Next.js' query object to match the URL's search params
        [...new URL(window.location.href).searchParams.keys()].forEach(key => {
          if (!new URL(urlString).searchParams.has(key)) {
            delete query[key];
          }
        });

        if (!isInitializedRef.current) {
          // replace if route has not been initialized yet to preserve history for back button
          void Router.replace({ pathname: Router.pathname, query }, urlString);
          isInitializedRef.current = true;
        } else {
          // push if route has been initialized to add to history
          void Router.push({ pathname: Router.pathname, query }, urlString, {
            shallow: true
          });
        }
      });
    }
  }, [showFinance, urlString]);
}
